import { Link, createFileRoute } from '@tanstack/react-router';
import dayjs from 'dayjs';
import { CloudDownload, Upload } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { ApiError } from '@/api/client';
import {
  useMerchants,
  useSettlements,
  useSupportedSettlementSources,
  useTriggerSettlementPull,
  useUploadSettlement,
} from '@/api/queries';
import type { PspCode } from '@/api/types';
import { Empty, ErrorBox } from '@/components/empty';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

export const Route = createFileRoute('/_authed/settlements/')({
  component: SettlementsList,
});

const PSPS: PspCode[] = ['sadad', 'moamalat', 'mobicash', 'tlync', 'adfali'];

function SettlementsList() {
  const { t } = useTranslation();
  const list = useSettlements();
  const merchants = useMerchants();
  const upload = useUploadSettlement();
  const supportedSources = useSupportedSettlementSources();
  const triggerPull = useTriggerSettlementPull();

  const [open, setOpen] = useState(false);
  const [pullOpen, setPullOpen] = useState(false);
  const [merchantId, setMerchantId] = useState<string>('');
  const [psp, setPsp] = useState<PspCode>('moamalat');
  const [file, setFile] = useState<File | null>(null);
  const [periodFrom, setPeriodFrom] = useState('');
  const [periodTo, setPeriodTo] = useState('');
  // Pull-trigger form state (separate from upload form so opening one
  // doesn't reset the other).
  const [pullMerchantId, setPullMerchantId] = useState<string>('');
  const [pullPsp, setPullPsp] = useState<PspCode | ''>('');
  const [pullFrom, setPullFrom] = useState('');
  const [pullTo, setPullTo] = useState('');

  const onTriggerPull = (e: React.FormEvent) => {
    e.preventDefault();
    if (!pullFrom || !pullTo) {
      toast.error(t('common.required') as string);
      return;
    }
    triggerPull.mutate(
      {
        merchant_id: pullMerchantId || undefined,
        psp: pullPsp || undefined,
        period_from: new Date(pullFrom).toISOString(),
        period_to: new Date(pullTo).toISOString(),
      },
      {
        onSuccess: () => {
          toast.success(t('settlements.pull_dialog.pulled_toast') as string);
          setPullOpen(false);
        },
        onError: (err) =>
          toast.error(
            err instanceof ApiError
              ? err.message
              : (t('settlements.pull_dialog.pull_failed') as string),
          ),
      },
    );
  };

  const onUpload = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!file || !merchantId) {
      toast.error(t('common.required') as string);
      return;
    }
    upload.mutate(
      {
        merchant_id: merchantId,
        psp,
        file,
        period_from: periodFrom || undefined,
        period_to: periodTo || undefined,
      },
      {
        onSuccess: (s) => {
          toast.success(
            t('settlements.upload_dialog.uploaded_toast', {
              rows: s.row_count,
              matched: s.matched,
            }) as string,
          );
          setFile(null);
          setOpen(false);
        },
        onError: (err) =>
          toast.error(
            err instanceof ApiError
              ? err.message
              : (t('settlements.upload_dialog.upload_failed') as string),
          ),
      },
    );
  };

  return (
    <div className="space-y-4">
      <header className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">{t('settlements.title')}</h1>
          <p className="text-sm text-muted-foreground">{t('settlements.subtitle')}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Dialog open={pullOpen} onOpenChange={setPullOpen}>
            <DialogTrigger asChild>
              <Button variant="outline">
                <CloudDownload className="h-4 w-4" /> {t('settlements.pull')}
              </Button>
            </DialogTrigger>
            <DialogContent>
              <form className="space-y-4" onSubmit={onTriggerPull}>
                <DialogHeader>
                  <DialogTitle>{t('settlements.pull_dialog.title')}</DialogTitle>
                  <DialogDescription>{t('settlements.pull_dialog.description')}</DialogDescription>
                </DialogHeader>
                <div className="space-y-1.5">
                  <Label htmlFor="pull-merchant">
                    {t('merchants.title')} ({t('common.optional')})
                  </Label>
                  <Select value={pullMerchantId} onValueChange={setPullMerchantId}>
                    <SelectTrigger id="pull-merchant">
                      <SelectValue placeholder={t('common.all') as string} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="">{t('common.all')}</SelectItem>
                      {merchants.data?.map((m) => (
                        <SelectItem key={m.id} value={m.id}>
                          {m.name} ({m.code})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="pull-psp">
                    {t('settlements.cols.psp')} ({t('common.optional')})
                  </Label>
                  <Select
                    value={pullPsp}
                    onValueChange={(v) => setPullPsp(v as PspCode | '')}
                  >
                    <SelectTrigger id="pull-psp">
                      <SelectValue placeholder={t('common.all') as string} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="">{t('common.all')}</SelectItem>
                      {(supportedSources.data ?? []).map((p) => (
                        <SelectItem key={p} value={p}>
                          {p}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid gap-3 grid-cols-2">
                  <div className="space-y-1.5">
                    <Label htmlFor="pull-from">{t('settlements.upload_dialog.period_from')}</Label>
                    <Input
                      id="pull-from"
                      type="datetime-local"
                      value={pullFrom}
                      onChange={(e) => setPullFrom(e.target.value)}
                      required
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="pull-to">{t('settlements.upload_dialog.period_to')}</Label>
                    <Input
                      id="pull-to"
                      type="datetime-local"
                      value={pullTo}
                      onChange={(e) => setPullTo(e.target.value)}
                      required
                    />
                  </div>
                </div>
                <DialogFooter>
                  <Button type="button" variant="ghost" onClick={() => setPullOpen(false)}>
                    {t('common.cancel')}
                  </Button>
                  <Button type="submit" disabled={triggerPull.isPending}>
                    {t('settlements.pull_dialog.submit')}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button>
              <Upload className="h-4 w-4" /> {t('settlements.upload')}
            </Button>
          </DialogTrigger>
          <DialogContent>
            <form className="space-y-4" onSubmit={onUpload}>
              <DialogHeader>
                <DialogTitle>{t('settlements.upload_dialog.title')}</DialogTitle>
                <DialogDescription>{t('settlements.upload_dialog.description')}</DialogDescription>
              </DialogHeader>
              <div className="space-y-1.5">
                <Label htmlFor="merchant">{t('settlements.upload_dialog.merchant_label')}</Label>
                <Select value={merchantId} onValueChange={setMerchantId}>
                  <SelectTrigger id="merchant">
                    <SelectValue placeholder={t('merchants.title') as string} />
                  </SelectTrigger>
                  <SelectContent>
                    {merchants.data?.map((m) => (
                      <SelectItem key={m.id} value={m.id}>
                        {m.name} ({m.code})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="psp">{t('settlements.upload_dialog.psp_label')}</Label>
                <Select value={psp} onValueChange={(v) => setPsp(v as PspCode)}>
                  <SelectTrigger id="psp">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PSPS.map((p) => (
                      <SelectItem key={p} value={p}>
                        {p}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid gap-3 grid-cols-2">
                <div className="space-y-1.5">
                  <Label htmlFor="from">
                    {t('settlements.upload_dialog.period_from')} ({t('common.optional')})
                  </Label>
                  <Input
                    id="from"
                    type="datetime-local"
                    value={periodFrom}
                    onChange={(e) => setPeriodFrom(e.target.value)}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="to">
                    {t('settlements.upload_dialog.period_to')} ({t('common.optional')})
                  </Label>
                  <Input
                    id="to"
                    type="datetime-local"
                    value={periodTo}
                    onChange={(e) => setPeriodTo(e.target.value)}
                  />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="file">{t('settlements.upload_dialog.file_label')}</Label>
                <Input
                  id="file"
                  type="file"
                  accept=".csv,text/csv"
                  onChange={(e) => setFile(e.target.files?.[0] ?? null)}
                />
              </div>
              <DialogFooter>
                <Button type="button" variant="ghost" onClick={() => setOpen(false)}>
                  {t('common.cancel')}
                </Button>
                <Button type="submit" disabled={upload.isPending || !file || !merchantId}>
                  {t('settlements.upload_dialog.submit')}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </header>

      {list.isError && <ErrorBox message={t('common.error')} />}

      <div className="rounded-md border border-border overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t('settlements.cols.uploaded')}</TableHead>
              <TableHead>{t('settlements.cols.psp')}</TableHead>
              <TableHead>{t('settlements.cols.file')}</TableHead>
              <TableHead>{t('settlements.cols.rows')}</TableHead>
              <TableHead>{t('settlements.cols.matched')}</TableHead>
              <TableHead>{t('settlements.cols.mismatch')}</TableHead>
              <TableHead>{t('settlements.cols.missing_in_hub')}</TableHead>
              <TableHead>{t('settlements.cols.missing_in_psp')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {list.data?.map((row) => (
              <TableRow key={row.id}>
                <TableCell className="font-mono text-xs whitespace-nowrap">
                  {dayjs(row.created_at).format('MMM D HH:mm')}
                </TableCell>
                <TableCell className="capitalize">{row.psp_code}</TableCell>
                <TableCell>
                  <Link
                    to="/settlements/$fileId"
                    params={{ fileId: row.id }}
                    className="font-mono text-xs hover:underline"
                  >
                    {row.filename}
                  </Link>
                </TableCell>
                <TableCell className="tabular-nums">{row.row_count}</TableCell>
                <TableCell>
                  <Badge variant="success">{row.matched_count}</Badge>
                </TableCell>
                <TableCell>
                  {row.mismatch_count > 0 ? (
                    <Badge variant="warning">{row.mismatch_count}</Badge>
                  ) : (
                    <span className="text-xs text-muted-foreground">0</span>
                  )}
                </TableCell>
                <TableCell>
                  {row.missing_in_hub_count > 0 ? (
                    <Badge variant="destructive">{row.missing_in_hub_count}</Badge>
                  ) : (
                    <span className="text-xs text-muted-foreground">0</span>
                  )}
                </TableCell>
                <TableCell>
                  {row.missing_in_psp_count > 0 ? (
                    <Badge variant="destructive">{row.missing_in_psp_count}</Badge>
                  ) : (
                    <span className="text-xs text-muted-foreground">0</span>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {!list.isLoading && list.data?.length === 0 && (
          <Empty title={t('settlements.none')} className="border-0" />
        )}
      </div>
    </div>
  );
}
